/**
 * Chaînes d'interface (chrome) des pages partenaires, par locale.
 *
 * Sépare le texte UI réutilisable du contenu éditorial (qui vit dans
 * app/data/partners/segments.ts). Permet aux vues FR et EN de partager le même
 * markup (zéro doublon de composant) en ne changeant que les libellés.
 */
import { type I18n } from "@/config/localeText";
import { COMMISSION_TIERS } from "@/config/partners";

/**
 * Ancre de section du formulaire de lead partenaire (cible des CTA hero).
 * Définie ICI (module neutre, ni serveur ni client) pour être importée À LA FOIS
 * par la vue serveur (PartnerSegmentView/PartnerHubView) ET le formulaire client
 * — un `export const` depuis un fichier "use client" se transforme en référence
 * client côté serveur (ne stringifie PAS en "partenariat"). À garder ici.
 */
export const PARTNER_LEAD_FORM_ID = "partenariat";

/**
 * Taux de commission par tranche, lus DEPUIS app/config/partners.ts (source
 * unique — jamais de 12/17/22 en dur). Indexés sur l'ordre des paliers :
 * [0] = tranche basse, [1] = intermédiaire, [2] = haute.
 */
const TIER_RATES = COMMISSION_TIERS.map((tier) => tier.rate);
const RATE_LOW = TIER_RATES[0] ?? null;
const RATE_MID = TIER_RATES[1] ?? null;
const RATE_HIGH = TIER_RATES[2] ?? null;

/** Valeur d'une option de type d'établissement (clé stable + libellé). */
export type PartnerFormOption = { value: string; label: string };
/** Option de volume annuel : clé stable, libellé, et taux de commission associé. */
export type PartnerFormVolumeOption = {
  value: string;
  label: string;
  /** Taux de commission (%) auquel cette tranche donne droit ; null = à définir. */
  rate: number | null;
};

/**
 * Chaînes du formulaire de capture de lead partenaire (Deliverable 3).
 * Toute chaîne visible du composant PartnerLeadForm vient d'ici.
 */
export type PartnerFormStrings = {
  /** En-tête / introduction de la section formulaire. */
  eyebrow: string;
  heading: string;
  subtitle: string;
  /** Libellés de champs. */
  typeLabel: string;
  typePlaceholder: string;
  typeOptions: PartnerFormOption[];
  establishmentLabel: string;
  establishmentPlaceholder: string;
  cityLabel: string;
  cityPlaceholder: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  volumeLabel: string;
  volumePlaceholder: string;
  volumeOptions: PartnerFormVolumeOption[];
  /** Indice de mappage tranche → commission (ex. « Commission estimée : 17 % »). */
  volumeTierHint: (rate: number) => string;
  messageLabel: string;
  messagePlaceholder: string;
  /** Message par défaut composé si la zone de message est vide (≥ 5 car. requis côté API). */
  defaultMessage: (input: {
    type: string;
    establishment: string;
    city: string;
  }) => string;
  honeypotLabel: string;
  /** Bouton + états. */
  submit: string;
  sending: string;
  successTitle: string;
  successMessage: string;
  errorMessage: string;
  /** Réassurance RGPD / confidentialité + consentement. */
  privacyNote: string;
  consentNote: string;
  /** Libellés requis (validation client). */
  requiredType: string;
  requiredEstablishment: string;
  requiredFirstName: string;
  requiredLastName: string;
  requiredEmail: string;
  invalidPhone: string;
};

export type PartnerUiStrings = {
  breadcrumbHome: string;
  breadcrumbChauffeur: string;
  breadcrumbPartners: string;
  forAudience: (audience: string) => string;
  factCommission: string;
  factCoverage: string;
  factCoverageValue: string;
  factAvailability: string;
  factAvailabilityValue: string;
  ctaBecomePartner: string;
  ctaAllPartnerships: string;
  commissionHeading: string;
  commissionSubtitle: string;
  whyHeading: string;
  processHeading: string;
  coverageLine: (cities: string) => string;
  faqHeading: (audience: string) => string;
  startHeading: string;
  startText: string;
  chooseProfileHeading: string;
  learnMore: string;
  hubEyebrow: string;
  hubTitle: string;
  hubLead: (maxRate: number) => string;
  allLiaisonsHeading: string;
  partnerTag: string;
  form: PartnerFormStrings;
};

export const PARTNER_UI: I18n<PartnerUiStrings> = {
  fr: {
    breadcrumbHome: "Accueil",
    breadcrumbChauffeur: "Chauffeur",
    breadcrumbPartners: "Partenaires",
    forAudience: (a) => `Pour les ${a}`,
    factCommission: "Commission",
    factCoverage: "Zone couverte",
    factCoverageValue: "Cannes · Nice · Monaco",
    factAvailability: "Disponibilité",
    factAvailabilityValue: "24/7, suivi de vol",
    ctaBecomePartner: "Devenir partenaire",
    ctaAllPartnerships: "Tous les partenariats",
    commissionHeading: "Grille de commission",
    commissionSubtitle:
      "Commission progressive sur le chiffre d'affaires annuel que vous apportez, versée sur l'encaissé réel.",
    whyHeading: "Pourquoi nous choisir",
    processHeading: "Comment se déroule le partenariat",
    coverageLine: (c) => `Zones couvertes : ${c} et l'aéroport de Nice.`,
    faqHeading: (a) => `Questions fréquentes — partenariat ${a}`,
    startHeading: "Lancez votre partenariat",
    startText:
      "Parlons de votre activité et mettons en place un partenariat clair, avec un interlocuteur dédié et une commission attractive.",
    chooseProfileHeading: "Choisissez votre profil",
    learnMore: "En savoir plus",
    hubEyebrow: "Programme partenaires — apporteurs d'affaires",
    hubTitle: "Devenez partenaire chauffeur privé sur la Côte d'Azur",
    hubLead: (max) =>
      `Conciergeries, hôtels et agences : proposez à vos clients un service de chauffeur privé premium à Cannes, Nice, Monaco et sur toute la Riviera, et percevez une commission jusqu'à ${max} % sur le chiffre d'affaires apporté.`,
    allLiaisonsHeading: "Toutes nos liaisons",
    partnerTag: "Partenariat",
    form: {
      eyebrow: "Devenir partenaire — apporteur d'affaires",
      heading: "Lancez votre partenariat",
      subtitle:
        "Renseignez votre établissement : nous revenons vers vous sous 48 h avec un partenariat clair, un interlocuteur dédié et votre grille de commission.",
      typeLabel: "Type d'établissement",
      typePlaceholder: "Sélectionnez votre activité",
      typeOptions: [
        { value: "conciergerie", label: "Conciergerie" },
        { value: "hotel", label: "Hôtel" },
        { value: "agence_voyage", label: "Agence de voyage" },
        { value: "agence_evenementiel", label: "Agence événementielle" },
        { value: "yacht", label: "Yacht / équipage" },
        { value: "location_villa", label: "Location de villa" },
        { value: "autre", label: "Autre" },
      ],
      establishmentLabel: "Nom de l'établissement",
      establishmentPlaceholder: "Ex. Conciergerie Riviera",
      cityLabel: "Ville / localisation",
      cityPlaceholder: "Ex. Cannes",
      firstNameLabel: "Prénom",
      firstNamePlaceholder: "Votre prénom",
      lastNameLabel: "Nom",
      lastNamePlaceholder: "Votre nom",
      emailLabel: "Email professionnel",
      emailPlaceholder: "vous@etablissement.com",
      phoneLabel: "Téléphone (facultatif)",
      phonePlaceholder: "+33 6 12 34 56 78",
      volumeLabel: "Volume / chiffre d'affaires annuel estimé",
      volumePlaceholder: "Sélectionnez une fourchette",
      volumeOptions: [
        { value: "< 10 000 €", label: "Moins de 10 000 €", rate: RATE_LOW },
        {
          value: "10 000 € – 40 000 €",
          label: "10 000 € – 40 000 €",
          rate: RATE_MID,
        },
        { value: "40 000 € et +", label: "40 000 € et plus", rate: RATE_HIGH },
        { value: "À définir", label: "Je ne sais pas encore", rate: null },
      ],
      volumeTierHint: (rate) => `Commission estimée : ${rate} %`,
      messageLabel: "Message (facultatif)",
      messagePlaceholder:
        "Parlez-nous de votre clientèle, de vos volumes et de vos attentes.",
      defaultMessage: ({ type, establishment, city }) =>
        `Demande de partenariat — ${type} ${establishment}${
          city ? ` à ${city}` : ""
        }.`,
      honeypotLabel: "Ne pas remplir",
      submit: "Envoyer ma demande",
      sending: "Envoi en cours…",
      successTitle: "Demande envoyée",
      successMessage:
        "Merci ! Votre demande de partenariat est bien reçue. Notre équipe vous recontacte sous 48 h.",
      errorMessage:
        "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
      privacyNote:
        "Vos informations restent confidentielles et ne servent qu'à étudier votre partenariat. Aucune transmission à des tiers.",
      consentNote:
        "En envoyant ce formulaire, vous acceptez d'être recontacté au sujet du programme partenaires.",
      requiredType: "Sélectionnez un type d'établissement",
      requiredEstablishment: "Le nom de l'établissement est requis",
      requiredFirstName: "Le prénom est requis (2 caractères minimum)",
      requiredLastName: "Le nom est requis (2 caractères minimum)",
      requiredEmail: "Un email valide est requis",
      invalidPhone: "Format de téléphone invalide",
    },
  },
  en: {
    breadcrumbHome: "Home",
    breadcrumbChauffeur: "Chauffeur",
    breadcrumbPartners: "Partners",
    forAudience: (a) => `For ${a}`,
    factCommission: "Commission",
    factCoverage: "Coverage",
    factCoverageValue: "Cannes · Nice · Monaco",
    factAvailability: "Availability",
    factAvailabilityValue: "24/7, flight tracking",
    ctaBecomePartner: "Become a partner",
    ctaAllPartnerships: "All partnerships",
    commissionHeading: "Commission scale",
    commissionSubtitle:
      "Progressive commission on the yearly revenue you refer, paid on amounts actually collected.",
    whyHeading: "Why choose us",
    processHeading: "How the partnership works",
    coverageLine: (c) => `Coverage: ${c} and Nice airport.`,
    faqHeading: (a) => `FAQ — ${a} partnership`,
    startHeading: "Start your partnership",
    startText:
      "Let's talk about your business and set up a clear partnership, with a dedicated contact and an attractive commission.",
    chooseProfileHeading: "Choose your profile",
    learnMore: "Learn more",
    hubEyebrow: "Partner program — referral partners",
    hubTitle: "Become a private chauffeur partner on the French Riviera",
    hubLead: (max) =>
      `Concierges, hotels and agencies: offer your clients a premium private chauffeur service in Cannes, Nice, Monaco and across the Riviera, and earn a commission of up to ${max}% on the revenue you refer.`,
    allLiaisonsHeading: "All our services",
    partnerTag: "Partnership",
    form: {
      eyebrow: "Become a partner — referral program",
      heading: "Start your partnership",
      subtitle:
        "Tell us about your establishment: we'll get back to you within 48h with a clear partnership, a dedicated contact and your commission scale.",
      typeLabel: "Type of establishment",
      typePlaceholder: "Select your business",
      typeOptions: [
        { value: "conciergerie", label: "Concierge service" },
        { value: "hotel", label: "Hotel" },
        { value: "agence_voyage", label: "Travel agency" },
        { value: "agence_evenementiel", label: "Event agency" },
        { value: "yacht", label: "Yacht / crew" },
        { value: "location_villa", label: "Villa rental" },
        { value: "autre", label: "Other" },
      ],
      establishmentLabel: "Establishment name",
      establishmentPlaceholder: "e.g. Riviera Concierge",
      cityLabel: "City / location",
      cityPlaceholder: "e.g. Cannes",
      firstNameLabel: "First name",
      firstNamePlaceholder: "Your first name",
      lastNameLabel: "Last name",
      lastNamePlaceholder: "Your last name",
      emailLabel: "Business email",
      emailPlaceholder: "you@establishment.com",
      phoneLabel: "Phone (optional)",
      phonePlaceholder: "+44 20 1234 5678",
      volumeLabel: "Estimated yearly volume / revenue",
      volumePlaceholder: "Select a range",
      volumeOptions: [
        { value: "< 10 000 €", label: "Under €10k", rate: RATE_LOW },
        { value: "10 000 € – 40 000 €", label: "€10k – €40k", rate: RATE_MID },
        { value: "40 000 € et +", label: "€40k and over", rate: RATE_HIGH },
        { value: "À définir", label: "Not sure yet", rate: null },
      ],
      volumeTierHint: (rate) => `Estimated commission: ${rate}%`,
      messageLabel: "Message (optional)",
      messagePlaceholder:
        "Tell us about your clientele, your volumes and your expectations.",
      defaultMessage: ({ type, establishment, city }) =>
        `Partnership request — ${type} ${establishment}${
          city ? ` in ${city}` : ""
        }.`,
      honeypotLabel: "Do not fill",
      submit: "Send my request",
      sending: "Sending…",
      successTitle: "Request sent",
      successMessage:
        "Thank you! Your partnership request has been received. Our team will get back to you within 48h.",
      errorMessage:
        "Something went wrong. Please try again or email us directly.",
      privacyNote:
        "Your details stay confidential and are only used to review your partnership. Never shared with third parties.",
      consentNote:
        "By sending this form, you agree to be contacted about the partner program.",
      requiredType: "Please select a type of establishment",
      requiredEstablishment: "Establishment name is required",
      requiredFirstName: "First name is required (min 2 characters)",
      requiredLastName: "Last name is required (min 2 characters)",
      requiredEmail: "A valid email is required",
      invalidPhone: "Invalid phone format",
    },
  },
  ar: {
    breadcrumbHome: "الرئيسية",
    breadcrumbChauffeur: "سائق خاص",
    breadcrumbPartners: "الشركاء",
    forAudience: (a) => `لـ${a}`,
    factCommission: "العمولة",
    factCoverage: "منطقة التغطية",
    factCoverageValue: "كان · نيس · موناكو",
    factAvailability: "التوفّر",
    factAvailabilityValue: "على مدار الساعة، تتبّع للرحلات",
    ctaBecomePartner: "كن شريكًا",
    ctaAllPartnerships: "جميع الشراكات",
    commissionHeading: "جدول العمولة",
    commissionSubtitle:
      "عمولة تصاعدية على الإيرادات السنوية التي تُحيلها، تُدفع على المبالغ المحصّلة فعليًّا.",
    whyHeading: "لماذا تختارنا",
    processHeading: "كيف تسير الشراكة",
    coverageLine: (c) => `المناطق المغطّاة: ${c} ومطار نيس.`,
    faqHeading: (a) => `أسئلة شائعة — شراكة ${a}`,
    startHeading: "أطلِق شراكتك",
    startText:
      "لنتحدّث عن نشاطك ونُرسِ شراكة واضحة، مع جهة اتصال مكرّسة وعمولة مغرية.",
    chooseProfileHeading: "اختر ملفّك",
    learnMore: "اعرف المزيد",
    hubEyebrow: "برنامج الشركاء — وسطاء الأعمال",
    hubTitle: "كن شريك سائق خاص على الريفييرا الفرنسية",
    hubLead: (max) =>
      `خدمات الكونسيرج والفنادق والوكالات: قدّموا لعملائكم خدمة سائق خاص راقية في كان ونيس وموناكو وعبر كامل الريفييرا، واحصلوا على عمولة تصل إلى ${max}٪ على الإيرادات التي تُحيلونها.`,
    allLiaisonsHeading: "جميع خدماتنا",
    partnerTag: "شراكة",
    form: {
      eyebrow: "كن شريكًا — برنامج وسطاء الأعمال",
      heading: "أطلِق شراكتك",
      subtitle:
        "عرِّفنا بمنشأتك: نعود إليك خلال 48 ساعة بشراكة واضحة، وجهة اتصال مكرّسة، وجدول عمولتك.",
      typeLabel: "نوع المنشأة",
      typePlaceholder: "اختر نشاطك",
      typeOptions: [
        { value: "conciergerie", label: "خدمة كونسيرج" },
        { value: "hotel", label: "فندق" },
        { value: "agence_voyage", label: "وكالة سفر" },
        { value: "agence_evenementiel", label: "وكالة تنظيم فعاليات" },
        { value: "yacht", label: "يخت / طاقم" },
        { value: "location_villa", label: "تأجير فيلات" },
        { value: "autre", label: "أخرى" },
      ],
      establishmentLabel: "اسم المنشأة",
      establishmentPlaceholder: "مثال: Riviera Concierge",
      cityLabel: "المدينة / الموقع",
      cityPlaceholder: "مثال: كان",
      firstNameLabel: "الاسم",
      firstNamePlaceholder: "اسمك",
      lastNameLabel: "اللقب",
      lastNamePlaceholder: "لقبك",
      emailLabel: "البريد الإلكتروني المهني",
      emailPlaceholder: "you@establishment.com",
      phoneLabel: "الهاتف (اختياري)",
      phonePlaceholder: "+33 6 12 34 56 78",
      volumeLabel: "الحجم / الإيرادات السنوية المقدّرة",
      volumePlaceholder: "اختر نطاقًا",
      volumeOptions: [
        { value: "< 10 000 €", label: "أقل من 10٬000 يورو", rate: RATE_LOW },
        {
          value: "10 000 € – 40 000 €",
          label: "10٬000 – 40٬000 يورو",
          rate: RATE_MID,
        },
        { value: "40 000 € et +", label: "40٬000 يورو فأكثر", rate: RATE_HIGH },
        { value: "À définir", label: "لا أعرف بعد", rate: null },
      ],
      volumeTierHint: (rate) => `العمولة المقدّرة: ${rate}٪`,
      messageLabel: "رسالة (اختياري)",
      messagePlaceholder: "حدّثنا عن عملائك وأحجامك وتوقّعاتك.",
      defaultMessage: ({ type, establishment, city }) =>
        `طلب شراكة — ${type} ${establishment}${city ? ` في ${city}` : ""}.`,
      honeypotLabel: "لا تملأ هذا الحقل",
      submit: "أرسِل طلبي",
      sending: "جارٍ الإرسال…",
      successTitle: "تم إرسال الطلب",
      successMessage:
        "شكرًا لك! تمّ استلام طلب الشراكة. سيعاود فريقنا التواصل معك خلال 48 ساعة.",
      errorMessage: "حدث خطأ ما. حاوِل مجدّدًا أو راسِلنا مباشرةً.",
      privacyNote:
        "تبقى معلوماتك سرّية وتُستخدم فقط لدراسة شراكتك. لا تُشارَك مع أي طرف ثالث.",
      consentNote:
        "بإرسالك هذا النموذج، توافق على معاودة التواصل معك بشأن برنامج الشركاء.",
      requiredType: "اختر نوع المنشأة",
      requiredEstablishment: "اسم المنشأة مطلوب",
      requiredFirstName: "الاسم مطلوب (حرفان كحدّ أدنى)",
      requiredLastName: "اللقب مطلوب (حرفان كحدّ أدنى)",
      requiredEmail: "يلزم بريد إلكتروني صالح",
      invalidPhone: "صيغة الهاتف غير صحيحة",
    },
  },
};
